import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Divider,
  ListItemButton,
  ListItemText,
  Collapse,
  List,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  Box,
} from '@mui/material';
import testLogo from '../../assets/Test.png';
import { requestLeaveConfirmation } from '../../library/UnsavedChangesGuard';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckIcon from '@mui/icons-material/Check';
import AddIcon from '@mui/icons-material/Add';
import SidebarItem from './SidebarItem';
import SftGlobalData from '../../library/SftGlobalData';

// Storage key for expanded sections
const STORAGE_KEY = 'sidebar_expanded_sections';

// Inside your component:
const LeftSidebarMenu = (props) => {
  const navigate = useNavigate();
  // Initialize expandedSections state
  const [expandedSections, setExpandedSections] = useState({});
  // Webshop switcher dialog (only relevant for the WEBSHOP group)
  const [webshopPickerOpen, setWebshopPickerOpen] = useState(false);
  
  // Load saved state from localStorage on component mount
  useEffect(() => {
    try {
      // Try to get saved state from localStorage
      const savedState = localStorage.getItem(STORAGE_KEY);
      
      if (savedState) {
        // If we have saved state, use it
        setExpandedSections(JSON.parse(savedState));
      } else if (props.modules && props.modules.length > 0) {
        // Otherwise initialize with all sections expanded
        const initialExpandedState = {};
        props.modules.forEach(module => {
          initialExpandedState[module.group] = true;
        });
        setExpandedSections(initialExpandedState);
        // Save the initial state
        localStorage.setItem(STORAGE_KEY, JSON.stringify(initialExpandedState));
      }
    } catch (error) {
      console.error('Error loading sidebar state:', error);
      // Fall back to default expanded state
      if (props.modules && props.modules.length > 0) {
        const fallbackState = {};
        props.modules.forEach(module => {
          fallbackState[module.group] = true;
        });
        setExpandedSections(fallbackState);
      }
    }
  }, [props.modules]);
  
  // Toggle expansion state for a section and save to localStorage
  const handleSectionClick = (event, groupName) => {
    // Stop event propagation to prevent double-trigger
    event.stopPropagation();
    
    setExpandedSections(prev => {
      const newState = {
        ...prev,
        [groupName]: !prev[groupName]
      };
      
      // Save updated state to localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      } catch (error) {
        console.error('Error saving sidebar state:', error);
      }
      
      return newState;
    });
  };
  
  
  const getModules = props.modules
    .map(function(data, idx) {
      if(SftGlobalData.debug){
        console.log("data.groupData: ", data);
      }

      const groupData = data.groupData.filter(group => group.active === 1);

      const sortedGroupData = data.preserveOrder
        ? [...groupData].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        : [...groupData].sort(function(a, b) {
            if(a.name.toLowerCase() < b.name.toLowerCase()) return -1;
            if(a.name.toLowerCase() > b.name.toLowerCase()) return 1;
            return 0;
          });

      const subGroupItems = sortedGroupData.map(function(groupData, idy){
        if(groupData.active === 1){
          return (
            <SidebarItem 
              className={props.theme} 
              key={idy} 
              title={groupData.name}  
              lang={props.lang} 
              image={testLogo} 
              isExpanded={props.menuExpanded} 
              toggleMobileOpen={()=>{props.toggleMobileOpen()}} 
              data={groupData} 
              theme={props.theme}
            />
          );
        } else {
          return <div key={idy}></div>;
        }
      });

      // Use the state value with a default of true if undefined
      const isExpanded = expandedSections[data.group] !== undefined 
        ? expandedSections[data.group] 
        : true;
        
      const hasContent = groupData?.length > 0;
      
      // Additional elements for the "other" group
      const additionalItems = [];
      if (data.group === "other" && props.screen !== "logging") {
        if (props.db?.is_owner == "1" && !props.is_sft) {
          additionalItems.push(
            <SidebarItem 
              className={props.theme} 
              key={"eticket"} 
              title={"E-ticket"}  
              lang={props.lang} 
              isExpanded={props.menuExpanded} 
              toggleMobileOpen={()=>{props.toggleMobileOpen()}} 
              data={{module: "eticket"}} 
              theme={props.theme}
            />
          );
          
          /*additionalItems.push(
            <SidebarItem 
              className={props.theme} 
              key={"peppol"} 
              title={"Peppol"}  
              lang={props.lang} 
              isExpanded={props.menuExpanded} 
              toggleMobileOpen={()=>{props.toggleMobileOpen()}} 
              data={{module: "peppol"}} 
              theme={props.theme}
            />
          );*/
        }
        
        if (props.group === "SUPER") {
          additionalItems.push(
            <SidebarItem 
              className={props.theme} 
              key={"EXACT"} 
              title={"Exact"}  
              lang={props.lang} 
              isExpanded={props.menuExpanded} 
              toggleMobileOpen={()=>{props.toggleMobileOpen()}} 
              data={{module: "exact"}} 
              theme={props.theme}
            />
          );
        }
        
        if (props.is_sft) {
          additionalItems.push(
            <SidebarItem 
              className={props.theme} 
              key={"RMA"} 
              title={"RMA"}  
              lang={props.lang} 
              isExpanded={props.menuExpanded} 
              toggleMobileOpen={()=>{props.toggleMobileOpen()}} 
              data={{module: "rma"}} 
              theme={props.theme}
            />
          );
        }

      }

      // For the WEBSHOP group, render the title with an inline switcher icon
      // (only when there's more than one webshop). The active shop's URL now
      // lives in the Header (under dbName) when the user is on a webshop
      // screen — see Header.js.
      const isWebshopGroup = data.group === "WEBSHOP";
      // Shown from ONE shop onwards: the dialog is also the entry point for
      // adding another webshop (a subsite with its own FTP creds + URL), so
      // gating it on >1 would make that unreachable for single-shop clients.
      const showSwitcher = isWebshopGroup && (props.availableWebshops?.length || 0) > 0;
      const primaryNode = isWebshopGroup ? (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
          <span>{data.group.toUpperCase()}</span>
          {showSwitcher && (
            <Tooltip title={props.t ? props.t('cfg_webshop_switch_or_add', 'Webshop wisselen of toevoegen') : 'Webshop wisselen of toevoegen'}>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setWebshopPickerOpen(true); }}
                sx={{ padding: 0, marginTop: '-3px' }}
              >
                <SwapHorizIcon sx={{ fontSize: '1.35rem' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) : data.group.toUpperCase();

      return (
        <div className={'sidebar_header ' + props.theme + (props?.testofzo === 7 ? " pink" : "")} key={idx}>
          {hasContent && (
            <>
              <ListItemButton
                onClick={(e) => handleSectionClick(e, data.group)}
                sx={{
                  padding: 0,
                  paddingLeft: 0.5,
                  paddingTop: 0.3,
                  paddingBottom: 0.3,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}
                disableRipple={false}
              >
                <ListItemText
                  primary={primaryNode}
                  primaryTypographyProps={{ component: 'div', sx: { fontFamily: 'inherit', fontWeight: 'bold', fontSize: '0.9em', lineHeight: 1.1 } }}
                />
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>

              <Divider />

              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {subGroupItems}
                  {additionalItems}
                </List>
              </Collapse>
            </>
          )}
        </div>
      );
    });
    
  return (
    <>
      {getModules}
      <Dialog
        open={webshopPickerOpen}
        onClose={() => setWebshopPickerOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{props.t ? props.t('cfg_webshop_select', 'Webshop selecteren') : 'Webshop selecteren'}</DialogTitle>
        <DialogContent dividers sx={{ padding: 0 }}>
          <List disablePadding>
            {(props.availableWebshops || []).map((w) => {
              const isActive = props.selectedWebshop?.id === w.id;
              return (
                <ListItemButton
                  key={w.id}
                  selected={isActive}
                  onClick={async () => {
                    // Switching webshop reloads every config screen from the
                    // new shop — confirm if there are buffered/unsaved edits.
                    if (!isActive && !(await requestLeaveConfirmation())) return;
                    props.setSelectedWebshop && props.setSelectedWebshop(w);
                    setWebshopPickerOpen(false);
                  }}
                >
                  <ListItemText
                    primary={w.display_name || w.slug}
                  />
                  {isActive && <CheckIcon fontSize="small" />}
                </ListItemButton>
              );
            })}
            <Divider />
            {/* Add another webshop — e.g. a subsite living in its own folder on
                the same FTP, with its own credentials and URL. Opens the setup
                wizard in forced-create mode (?new=1); the backend already
                supports N shops per client (unique on client+db+slug). */}
            <ListItemButton
              onClick={async () => {
                if (!(await requestLeaveConfirmation())) return;
                setWebshopPickerOpen(false);
                props.changeScreen && props.changeScreen('webshop-setup');
                navigate('/' + props.lang + '/webshop-setup?new=1');
              }}
            >
              <AddIcon fontSize="small" sx={{ mr: 1 }} />
              <ListItemText primary={props.t ? props.t('cfg_webshop_add', 'Webshop toevoegen') : 'Webshop toevoegen'} />
            </ListItemButton>
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeftSidebarMenu;